import {
  delay
} from 'redux-saga'
import {
  select,
  fork,
  put,
  all,
  call,
  takeEvery,
  cancelled,
} from 'redux-saga/effects'
import T from 'i18n-react'
import formatSize from 'helpers/FormatSize'
import {
  keysToCamelCase,
  formDataHeader,
  buildFormData
} from 'helpers/RequestUtil'
import axios from 'axios'

import {
  updateTables,
} from 'containers/Entity/actions'

import {
  PAGE,
  CHANGE_ACTIVE,
  CHANGE_PAGE,
  DROP_REJECTED,
  POST_UPLOAD_FILE,
  MAX_SIZE,
  FILE_TYPE_STRING
} from './constants'

import {
  activeSelector,
  pageSelector,
  fileTypeSelector,
  fileTypeLabelSelector,
  isUploadingSelector,
  internalSelector,
} from './selectors'

import {
  projectIdSelector,
  workflowLifecycleSelector,
} from 'containers/Project/View/selectors'

import {
  resetActive,
  resetPage,
  resetFileType,
  resetInternal,
  updateActive,
  updatePage,
  updateFileType,
  updateInternal,
  changePage,
  changeActive,
  setActive,
  setInactive,
  setFileTypePage,
  setUploadPage,
  setLoadingPage,
  postUploadFileStart,
  postUploadFileSuccess,
  postUploadFileError,
} from './actions'

import {
  fetchProjectWorkflowDetails
} from 'containers/Project/LifecycleSection/actions'

import { getWorkflowDetails } from 'containers/Project/View/actions'

import {
  error,
  success
} from 'containers/App/Notification/actions'

import {
  fetchFileForThumbnail
} from 'containers/Project/Thumbnail/actions'

function* reset() {
  yield put(resetActive())
  yield put(resetPage())
  yield put(resetFileType())
  yield put(resetInternal())
}

function* postUploadFile(file, type, id, internal){
  try {
    yield put(postUploadFileStart())
    yield put(setLoadingPage())
    const lifecycle = yield select(workflowLifecycleSelector)
    const config = formDataHeader()
    const fileData = buildFormData({
      file,
      type,
      name: file.name,
      id,
      internal
    })
    const { data } = yield call(
      axios.post,
      '/api/fulcrum/project/file_upload',
      fileData,
      config
    )
    const formattedData = keysToCamelCase(data)
    yield put(updateTables({
      result: {
        project_files: [formattedData.id]
      },
      entities: {
        project_files: {
          [formattedData.id]: formattedData
        }
      }
    }))

    yield put(postUploadFileSuccess())
    yield put(success(T.translate(
      'pages.project.view.notification.post_upload_success',
      { name: file.name }
    )))
    if ((type == 'Schematic' || type == 'Layout') && lifecycle == 'Kickoff'){
      yield put(success(T.translate(
        'project.lifecycle.transition.to_design_review'
      )))
    }
    else if (type == 'Software Checklist' && lifecycle == 'Design Review'){
      yield put(success(T.translate(
        'project.lifecycle.transition.to_qualification'
      )))
    }
    yield put(fetchProjectWorkflowDetails(id))
    yield put(getWorkflowDetails())
    yield fork(reset)
  } catch(err) {
    yield put(error(T.translate(
      'pages.project.view.notification.post_upload_error',
      {
        name: file.name,
        size: formatSize(MAX_SIZE),
      }
    )))
    yield put(postUploadFileError(err))
    yield put(setUploadPage())
  } finally {
    // This shouldn't happen
    if (yield cancelled()) {
      yield put(error(T.translate('pages.project.view.notification.upload_error')))
      yield fork(reset)
    }
  }
}

function* handleChangePage(action) {
  const { page } = action
  yield put(updatePage(page))
}

function* handleChangeActive(action) {
  const { active } = action
  const isUploading = yield select(isUploadingSelector)
  if ( !isUploading ) {
    yield put(updateActive(active))
    if (!active) {
      yield fork(reset)
    }
  }
}

function* handleDropRejected(action) {
  const { files: [{ name }] } = action
  yield put(error(T.translate(
    'pages.project.view.notification.drop_rejected',
    {
      name,
      size: formatSize(MAX_SIZE),
      types: FILE_TYPE_STRING.join(', ')
    }
  )))
}

function* handlePostUploadFile(action) {
  const { files: [file] } = action
  const type = yield select(fileTypeLabelSelector)
  const id = yield select(projectIdSelector)
  const internal = yield select(internalSelector)
  yield call(postUploadFile, file, type, id, internal)
  yield put(fetchFileForThumbnail())
}

function* watchChangeActive() {
  yield takeEvery(CHANGE_ACTIVE, handleChangeActive)
}

function* watchChangePage() {
  yield takeEvery(CHANGE_PAGE, handleChangePage)
}

function* watchDropRejected() {
  yield takeEvery(DROP_REJECTED, handleDropRejected)
}

function* watchPostUploadFile() {
  yield takeEvery(POST_UPLOAD_FILE, handlePostUploadFile)
}

function* loadSagas() {
  yield all([
    fork(watchChangeActive),
    fork(watchChangePage),
    fork(watchDropRejected),
    fork(watchPostUploadFile),
  ])
}

export default loadSagas
